export function formatEventDate(dateStr) {
  if (!dateStr) return "Date to be announced";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export function formatShortDate(dateStr) {
  if (!dateStr) return "TBA";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export function formatTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

// "10 December 2026" — day-month-year, used for multi-day ranges.
function formatDayMonthYear(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return dateStr;
  }
}

// Single day => full weekday date (e.g. "Thursday, December 10, 2026").
// Multi-day => "10 December 2026 – 12 December 2026".
export function formatDateRange(startStr, endStr) {
  if (!startStr) return "Date to be announced";
  if (!endStr || endStr === startStr) return formatEventDate(startStr);
  return `${formatDayMonthYear(startStr)} – ${formatDayMonthYear(endStr)}`;
}

// "" when no start time, "6:00 PM" with start only, "6:00 PM – 10:00 PM" with both.
export function formatTimeRange(startStr, endStr) {
  if (startStr && endStr) return `${formatTime(startStr)} – ${formatTime(endStr)}`;
  if (startStr) return formatTime(startStr);
  if (endStr) return `Until ${formatTime(endStr)}`;
  return "";
}

export function countdown(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + "T00:00:00").getTime();
  const diff = target - Date.now();
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return { days, hours, mins };
}

export function buildIcs(event) {
  const dt = (event.date || "").replace(/-/g, "");
  const tm = (event.time || "18:00").replace(":", "") + "00";
  const start = `${dt}T${tm}`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//AuraRSVP//EN",
    "BEGIN:VEVENT",
    `UID:${event.id}@aurarsvp`,
    `DTSTART:${start}`,
    `SUMMARY:${event.title}`,
    `LOCATION:${event.venue_name || ""} ${event.address || ""}`,
    `DESCRIPTION:${(event.description || "").replace(/\n/g, " ")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
}

export const ITINERARY_TEMPLATES = {
  "Wedding": [
    { time: "4:00 PM", title: "Wedding Ceremony", location: "Main Lawn", description: "Traditional ceremony with family and friends", notes: "" },
    { time: "6:00 PM", title: "Cocktails", location: "Poolside", description: "Cocktails, refreshments and socializing", notes: "" },
    { time: "7:30 PM", title: "Dinner", location: "Banquet Hall", description: "Dinner and live entertainment", notes: "" },
    { time: "9:00 PM", title: "Speeches & Toasts", location: "Main Stage", description: "Family speeches and special messages", notes: "" },
  ],
  "Reunion": [
    { time: "11:00 AM", title: "Welcome & Registration", location: "Entrance", description: "Check-in and welcome drinks", notes: "" },
    { time: "12:30 PM", title: "Group Lunch", location: "Garden", description: "Shared meal and catching up", notes: "" },
    { time: "3:00 PM", title: "Games & Activities", location: "Lawn", description: "Fun activities for all ages", notes: "" },
    { time: "6:00 PM", title: "Group Photo & Dinner", location: "Hall", description: "Memories and a shared dinner", notes: "" },
  ],
  "Sports Event": [
    { time: "9:00 AM", title: "Registration", location: "Gate 1", description: "Participant check-in", notes: "" },
    { time: "10:00 AM", title: "Opening Ceremony", location: "Main Ground", description: "Welcome and warm-up", notes: "" },
    { time: "11:00 AM", title: "First Match", location: "Field A", description: "Opening fixtures", notes: "" },
    { time: "1:00 PM", title: "Lunch Break", location: "Food Court", description: "", notes: "" },
    { time: "3:00 PM", title: "Finals", location: "Main Ground", description: "Championship match", notes: "" },
    { time: "5:00 PM", title: "Prize Distribution", location: "Main Stage", description: "Awards and closing", notes: "" },
  ],
  "Live Show": [
    { time: "6:00 PM", title: "Gates Open", location: "Main Entrance", description: "Doors open, seating begins", notes: "" },
    { time: "7:00 PM", title: "Opening Act", location: "Stage", description: "Supporting performance", notes: "" },
    { time: "8:30 PM", title: "Main Performance", location: "Stage", description: "Headline act", notes: "" },
    { time: "10:30 PM", title: "Closing", location: "Stage", description: "Encore and wrap-up", notes: "" },
  ],
  "Birthday": [
    { time: "5:00 PM", title: "Guests Arrive", location: "Venue", description: "Welcome drinks and music", notes: "" },
    { time: "6:00 PM", title: "Cake Cutting", location: "Main Hall", description: "The big moment", notes: "" },
    { time: "6:30 PM", title: "Dinner & Celebration", location: "Dining Area", description: "Food, music and dancing", notes: "" },
  ],
  "Anniversary": [
    { time: "6:30 PM", title: "Welcome & Toast", location: "Lounge", description: "Celebratory toast", notes: "" },
    { time: "7:30 PM", title: "Dinner", location: "Dining Hall", description: "Candlelit dinner", notes: "" },
    { time: "9:00 PM", title: "Dancing", location: "Ballroom", description: "Music and dancing", notes: "" },
  ],
  "Corporate": [
    { time: "9:00 AM", title: "Registration & Coffee", location: "Foyer", description: "Check-in and networking", notes: "" },
    { time: "10:00 AM", title: "Keynote", location: "Auditorium", description: "Opening keynote address", notes: "" },
    { time: "1:00 PM", title: "Networking Lunch", location: "Dining Hall", description: "", notes: "" },
    { time: "4:00 PM", title: "Awards & Closing", location: "Main Stage", description: "Recognition and wrap-up", notes: "" },
  ],
};

function icsTime(dateStr, timeStr) {
  const d = (dateStr || "").replace(/-/g, "");
  let h = 18, m = 0;
  const mt = (timeStr || "").trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (mt) {
    h = parseInt(mt[1], 10);
    m = mt[2] ? parseInt(mt[2], 10) : 0;
    const ap = mt[3] ? mt[3].toLowerCase() : null;
    if (ap === "pm" && h < 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
  }
  return `${d}T${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
}

export function buildScheduleIcs(event, items) {
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Godinga//Itinerary//EN"];
  (items || []).forEach((it, i) => {
    const day = it.day || event.date;
    if (!day) return;
    lines.push(
      "BEGIN:VEVENT",
      `UID:${event.id}-${i}@godinga`,
      `DTSTART:${icsTime(day, it.time)}`,
      `SUMMARY:${(it.title || "Activity").replace(/\n/g, " ")} - ${event.title}`,
      `LOCATION:${(it.location || event.venue_name || "").replace(/\n/g, " ")}`,
      `DESCRIPTION:${(it.description || "").replace(/\n/g, " ")}`,
      "END:VEVENT"
    );
  });
  lines.push("END:VCALENDAR");
  return "data:text/calendar;charset=utf-8," + encodeURIComponent(lines.join("\r\n"));
}

export function printItinerary(event, groups) {
  const w = window.open("", "_blank");
  if (!w) return;
  const rows = groups.map((g) =>
    `<h2 style="font-family:Georgia,serif;color:#9a7d28;margin:28px 0 10px;font-size:18px">${g.label}</h2>` +
    g.items.map((it) =>
      `<div style="display:flex;gap:16px;padding:12px 0;border-bottom:1px solid #eee">
        <div style="width:90px;font-weight:700;color:#9a7d28">${it.time || ""}</div>
        <div><div style="font-weight:600">${it.title || ""}</div>
        ${it.description ? `<div style="color:#555;font-size:13px;margin-top:2px">${it.description}</div>` : ""}
        ${it.location ? `<div style="color:#777;font-size:13px;margin-top:2px">\u{1F4CD} ${it.location}</div>` : ""}
        ${it.notes ? `<div style="color:#999;font-size:12px;font-style:italic;margin-top:2px">${it.notes}</div>` : ""}
        </div></div>`).join("")
  ).join("");
  w.document.write(
    `<html><head><title>${event.title} - Itinerary</title></head>
    <body style="font-family:Arial,Helvetica,sans-serif;max-width:640px;margin:40px auto;padding:0 20px;color:#1a1a17">
    <h1 style="font-family:Georgia,serif;font-size:28px;margin:0">${event.title}</h1>
    <p style="color:#666;margin:6px 0 0">${event.venue_name || ""}</p>
    ${rows}
    <p style="color:#aaa;font-size:11px;margin-top:40px;text-align:center">Powered by Godinga</p>
    </body></html>`
  );
  w.document.close();
  setTimeout(() => w.print(), 400);
}


export const COVER_PRESETS = [
  { id: "wedding", title: "Grand Ballroom Gala", url: "https://images.unsplash.com/photo-1524824267900-2fa9cbf7a506?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  { id: "banquet", title: "Chandelier Elegance", url: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  { id: "floral", title: "Floral Canopy", url: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  { id: "crystal", title: "Crystal & Candlelight", url: "https://images.pexels.com/photos/12689014/pexels-photo-12689014.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" },
  { id: "dining", title: "Botanical Fine Dining", url: "https://images.pexels.com/photos/16120239/pexels-photo-16120239.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" },
  { id: "party", title: "Golden Celebration", url: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
];
